import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { createAsyncThunk } from "@reduxjs/toolkit";
import type { BtSlimParticipant } from '@/bt-common/schemas/wa/participant';
import moment from 'moment';
import { CommonRootState } from "../../../common/types/common-root-state-type";
import { TeamInboxChatsCacheReducerActions, TeamInboxMessagesCacheReducerActions, DBWaContactsCacheThunks, DBWaNoteThunks, DBReminderThunks, DBBotThunks } from "../../../common/reducers/DBServiceThunks";
import { TeamInboxActions } from "./TeamInboxReducer";
import { isGroupChat, makeSelectDisplayNamesByContactIds, selectChatById, selectCurrentWaChat } from "./TeamInboxSelectors";
import { normalizeChat, isSystemNotificationMessage, mimeForMessageType } from "../team-inbox-helpers";
import { BtSlimChat } from "@/bt-common/schemas/wa/chat";
import { selectWhatsappContactById, shouldGetMoreContactsSelector } from "../../../common/reducers/WhatsAppSelectors";
import { setCurrentChatThunk } from "@extension/context/content/thunks/WhatsappThunks";
import { WA_TOOL_COMMANDS } from "@/bt-common/interfaces/whatsapp-tools-commands.interface";
import {
    waQueryContactsAndChatsTool,
    waGetChatObjectByChatIdTool,
    waSendSeenTool,
    waGetMessageMediaTool,
    getWaMeInfoTool,
    WhatsappToolsClientActions,
    fetchAndCacheProfilePicUrlsThunk,
    triggerRequesthWhatsappTool,
} from "@common/tools/whatsapp";
import { apiCall } from "@extension/wa-communication-layer/api/api-call-helper";
import { canonicalMsgKey } from "@common/services/message-vault";
import { isSendSeenEnabled } from "../send-seen-preference";

// ── Backup full-media map (per chat) ────────────────────────────────────────
// The vault holds only a THUMBNAIL for images/documents, but the server backup
// holds the FULL media. Media priority is: live WhatsApp > backup (full) > vault
// (thumbnail) — so a row served by the vault should still render the backup's
// full-res media when the message is backed up. We fetch the chat's backup rows
// ONCE (they carry short-lived signed `mediaUrl`s) and cache msgId→url,
// addressing-agnostically, so requestMessageMediaThunk can upgrade a vault/backup
// thumbnail to full-res without a per-message round-trip.
const backupMediaUrlCache = new Map<string, { at: number; map: Map<string, string> }>();
const backupMediaMapInflight = new Map<string, Promise<Map<string, string>>>();
const BACKUP_MEDIA_MAP_TTL = 5 * 60 * 1000; // signed URLs last ~1h; refresh the map every 5 min

const loadBackupMediaMap = (chatId: string): Promise<Map<string, string>> => {
    const cached = backupMediaUrlCache.get(chatId);
    if (cached && Date.now() - cached.at <= BACKUP_MEDIA_MAP_TTL) return Promise.resolve(cached.map);
    // Dedupe concurrent loads: a chat open fires requestMessageMediaThunk for
    // every visible media row at once — they all share ONE backup fetch.
    let inflight = backupMediaMapInflight.get(chatId);
    if (!inflight) {
        inflight = (async () => {
            const map = new Map<string, string>();
            try {
                const rows = await apiCall<any[]>('get', `/wa-messages-backup?chatId=${encodeURIComponent(chatId)}&limit=500`);
                (Array.isArray(rows) ? rows : []).forEach((r) => {
                    if (r?.mediaUrl && r?.msgId) map.set(canonicalMsgKey(r.msgId), r.mediaUrl);
                });
            } catch (err) {
                console.warn('[team-inbox] backup media-map fetch failed', { chatId, err });
            }
            backupMediaUrlCache.set(chatId, { at: Date.now(), map });
            backupMediaMapInflight.delete(chatId);
            return map;
        })();
        backupMediaMapInflight.set(chatId, inflight);
    }
    return inflight;
};

const getBackupMediaUrl = async (chatId: string, msgId: string): Promise<string | null> => {
    if (!chatId || !msgId) return null;
    const map = await loadBackupMediaMap(chatId);
    return map.get(canonicalMsgKey(msgId)) || null;
};

// ── Chat-list request sequencing ────────────────────────────────────────────
// The chats cache is mutated by several async fetches (search queries, full
// list reloads, pagination). Responses can resolve out of order — a slow
// earlier query must never overwrite the results of a newer one. Every fetch
// that (re)builds the list claims a monotonic request id via
// beginChatListRequest(); a response is applied only while its id is still
// the latest. Passive fetches (load-more, empty-list retry) snapshot the
// current id with currentChatListRequest() so they get invalidated by any
// newer search/reload without invalidating anything themselves.
let chatListRequestSeq = 0;
export const beginChatListRequest = (): number => ++chatListRequestSeq;
export const currentChatListRequest = (): number => chatListRequestSeq;
export const isChatListRequestCurrent = (requestId: number): boolean => requestId === chatListRequestSeq;

// ── Open chat (single entry point) ──────────────────────────────────────────
// Mirrors what the extension does on WhatsApp Web when a chat opens
// (`open_chat` → setCurrentChatThunk): every chat-scoped feature — labels,
// notes, reminders, quick agents, tasks, AI assistant — reads
// WhatsAppReducer.currentChat, so Team Inbox sets the exact same state and
// the feature components run unchanged. All Team Inbox click paths (chat
// rows, contact search results) must go through this thunk.
export const openTeamInboxChatThunk = createAsyncThunk<
    void,
    { chatId: string; tempChatData?: any },
    { state: CommonRootState }
>("teamInbox/openChat", async ({ chatId, tempChatData }, thunkAPI) => {
    const { dispatch, getState } = thunkAPI;

    dispatch(TeamInboxActions.handleClickOnChat({ type: "chat", chatId, tempChatData }));

    // Notes/reminders/labels are persisted against the @c.us id. Tool output
    // is already @c.us by wire convention; resolve only if a legacy @lid id
    // leaks in (e.g. from an old cache entry).
    let resolvedChatId = chatId;
    if (chatId.endsWith('@lid')) {
        try {
            const result = await triggerRequesthWhatsappTool<{ waLidSerialized: string }, { convertedId?: string }>(
                WA_TOOL_COMMANDS.CONVERT_WA_LID_SERIALIZED_TO_CUS_SERIALIZED,
                { waLidSerialized: chatId }
            );
            if (result?.success && result.data?.convertedId) {
                resolvedChatId = result.data.convertedId;
            }
        } catch { /* keep the lid id — a stale-empty lookup beats a crash */ }
    }

    // The same thunk WhatsApp Web runs when a chat opens (also subscribes the
    // chat's task board to the notifier).
    const waChat = selectChatById(getState(), chatId);
    const name = waChat?.name || tempChatData?.name || tempChatData?.displayName || '';
    dispatch(setCurrentChatThunk({
        chat: {
            id: { _serialized: resolvedChatId },
            name,
            isGroup: chatKindOf(resolvedChatId) === 'group',
            formattedTitle: name,
            checked: false,
            displayName: name,
        } as any,
    }));

    // Pre-fetch chat-scoped data so sidebar panels and badge counts are ready
    // (mirrors the extension's tasks-action-bar behavior).
    dispatch(DBWaNoteThunks.find({ chatId: resolvedChatId, $sort: { createdAt: -1 } }));
    dispatch(DBReminderThunks.find({}));
    dispatch(DBBotThunks.find({ $paginate: false }));

    // WA marks a chat read the moment it opens: clear the local badge and
    // send-seen to the phone (no-op when unreadCount is already 0).
    dispatch(markActiveChatAsSeenIfNeeded({ chatId }));
});

// Global abort controllers for cancelling pending media requests
let mediaRequestAbortControllers: Map<string, AbortController> = new Map();

// Track which chat profile pics we've already requested to avoid duplicate requests
const requestedChatProfilePics = new Set<string>();
// Track pending profile pic request to cancel if new one comes in
let pendingProfilePicRequest: AbortController | null = null;

// Function to cancel all pending media requests
export const cancelPendingMediaRequests = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    "cancelPendingMediaRequests",
    async (_, thunkAPI) => {
        const { dispatch } = thunkAPI;

        mediaRequestAbortControllers.forEach((controller) => {
            controller.abort();
        });
        mediaRequestAbortControllers.clear();

        dispatch(TeamInboxMessagesCacheReducerActions.setItemsLoading(false));
    }
);


export const requestMessageMediaThunk = createAsyncThunk<
    void,
    string,
    { state: CommonRootState }
>(
    "requestMessageMediaThunk",
    async (messageId, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const state = thunkAPI.getState();

        // Check if message still exists in cache (i.e., chat hasn't changed)
        const message = state.DBTeamInboxMessagesCacheReducer?.entities?.[messageId];
        if (!message) {
            console.log('[team-inbox] requestMessageMediaThunk - message no longer in cache, skipping', { messageId });
            return;
        }

        // Check if media is already loading or loaded
        if (message.mediaStatus === 'loading' || message.mediaStatus === 'loaded') {
            console.log('[team-inbox] requestMessageMediaThunk - media already loading/loaded, skipping', { messageId, mediaStatus: message.mediaStatus });
            return;
        }

        // Vault / DB-backup tiers: the full media bytes aren't in the live WA
        // store (so the engine fetch below would fail). Media priority here is
        // BACKUP (full) > vault THUMBNAIL: the vault only keeps a thumbnail (in
        // `body` as base64 — a WAWeb quirk), but if the message is backed up the
        // FULL bytes live in the bucket behind a signed URL. So prefer the backup
        // media and fall back to the thumbnail only when it isn't backed up —
        // this is what makes offline (WhatsApp-off) media full-quality whenever
        // it was backed up, regardless of whether the vault or DB tier served the row.
        const isBackupTier = message.source === 'vault' || message.source === 'backup';
        const body: string = typeof message.body === 'string' ? message.body : '';
        const b64Mime = body.startsWith('/9j/') ? 'image/jpeg' : body.startsWith('iVBOR') ? 'image/png' : null;
        if (isBackupTier) {
            const waId = message.waMessageId || message.waMessageKey?._serialized || messageId;
            const backupUrl = await getBackupMediaUrl(message.chatId, waId);
            // Chat may have changed during the fetch — updateOne on a missing id
            // is a harmless no-op, so no re-check needed.
            if (backupUrl) {
                // Include a mimetype so the attachment preview knows it's an image/
                // video/doc — without it `asset.type` is empty and the bubble renders
                // blank despite the full-res URL being set.
                const mimetype = mimeForMessageType(message.type, message.filename);
                dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
                    id: messageId,
                    changes: {
                        mediaStatus: 'loaded',
                        mediaData: { url: backupUrl, mediaUrl: backupUrl, mimetype, filename: message.filename ?? undefined },
                    },
                }));
                return;
            }
            if (b64Mime) {
                const dataUri = `data:${b64Mime};base64,${body}`;
                dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
                    id: messageId,
                    changes: {
                        mediaStatus: 'loaded',
                        mediaData: { url: dataUri, mediaUrl: dataUri, mimetype: b64Mime, isThumbnail: true },
                    },
                }));
                return;
            }
        }

        // Cancel any existing request for this message
        const existingController = mediaRequestAbortControllers.get(messageId);
        if (existingController) {
            existingController.abort();
        }

        // Create new abort controller for this media request
        const controller = new AbortController();
        mediaRequestAbortControllers.set(messageId, controller);

        // Get the actual WhatsApp message ID - use waMessageId field if available (set during message normalization)
        // Fall back to id._serialized or other ID fields, and finally to the cache key (messageId)
        // The tool needs the real WhatsApp ID to fetch media, not our generated cache key
        const waMessageId = message.waMessageId || message.id?._serialized || message.key?._serialized || message.key?.id || messageId;

        // If waMessageId is the same as messageId and looks like a generated ID, media fetch will likely fail
        const isGeneratedId = waMessageId === messageId && messageId.includes('_') && !messageId.includes('@');
        if (isGeneratedId) {
            console.warn('[team-inbox] requestMessageMediaThunk - using generated ID, media fetch may fail', { messageId });
        }

        console.log('[team-inbox] requestMessageMediaThunk', { messageId, waMessageId });

        dispatch(TeamInboxMessagesCacheReducerActions.updateOne({ id: messageId, changes: { mediaStatus: 'loading' } }));

        // waGetMessageMediaTool with mode:'full' uploads the media to a signed GCS URL
        // and returns { mediaFile: { mediaUrl, mimetype, ... }, thumbnailFile: { mediaUrl, ... } }.
        try {
            const result = await waGetMessageMediaTool({ waMessageKey: waMessageId, mode: 'full' });

            if (!result?.success) {
                console.error('[team-inbox] waGetMessageMediaTool returned failure', { messageId, error: result.success === false ? result.error : undefined });
                dispatch(TeamInboxMessagesCacheReducerActions.updateOne({ id: messageId, changes: { mediaStatus: 'error' } }));
                return;
            }
            // Page handler emits `{mediaFile, thumbnailFile}` for mode:'full';
            // thunk return is `WaToolResponse<unknown>` (no contract yet — see
            // KNOWN DRIFT note on the ClientAction). Narrow defensively here.
            type MediaFullPayload = { mediaFile?: { mediaUrl?: string; mimetype?: string; filename?: string; fileName?: string; fileSize?: number; size?: number }; thumbnailFile?: { mediaUrl?: string } };
            const payload = (result.data ?? {}) as MediaFullPayload;
            const mediaFile = payload.mediaFile;
            const thumbFile = payload.thumbnailFile;
            const mediaUrl = mediaFile?.mediaUrl;
            if (mediaUrl) {
                dispatch(TeamInboxMessagesCacheReducerActions.updateOne({
                    id: messageId,
                    changes: {
                        mediaStatus: 'loaded',
                        mediaData: {
                            url: mediaUrl,
                            mediaUrl,
                            thumbnailUrl: thumbFile?.mediaUrl,
                            mimetype: mediaFile?.mimetype,
                            filename: mediaFile?.filename ?? mediaFile?.fileName,
                            fileSize: mediaFile?.fileSize ?? mediaFile?.size,
                        },
                    },
                }));
            } else {
                dispatch(TeamInboxMessagesCacheReducerActions.updateOne({ id: messageId, changes: { mediaStatus: 'error' } }));
            }
        } catch (error: any) {
            if (controller.signal.aborted || error?.name === 'AbortError') {
                console.log('[team-inbox] Media request was cancelled', { messageId });
                return;
            }
            console.error('[team-inbox] waGetMessageMediaTool failed', { messageId, error });
            dispatch(TeamInboxMessagesCacheReducerActions.updateOne({ id: messageId, changes: { mediaStatus: 'error' } }));
        } finally {
            mediaRequestAbortControllers.delete(messageId);
        }
    }
)


export const requestSendSeenByChatIdThunk = createAsyncThunk<
    void,
    string,
    { state: CommonRootState }
>(
    "requestSendSeenByChatIdThunk",
    async (chatId, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;

        // Debug opt-out: browsing the inbox must not touch the phone's unread
        // state when the read-receipt toggle is off.
        if (!isSendSeenEnabled()) return;

        console.log('[team-inbox] requestSendSeenByChatIdThunk', { chatId });

        // waSendSeenTool handles dual-mode (extension/server) internally - DRY principle
        try {
            await waSendSeenTool({ chatId });
        } catch (error) {
            console.error('[team-inbox] waSendSeenTool failed', { chatId, error });
        }
    }
)

export const markActiveChatAsSeenIfNeeded = createAsyncThunk<
    void,
    { chatId?: string } | undefined,
    { state: CommonRootState }
>(
    "chat/markActiveChatAsSeenIfNeeded",
    async (arg, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const state = thunkAPI.getState();
        // Explicit chatId (chat-open path) → open-chat id → WhatsAppReducer
        // currentChat. The last two cover the legacy virtuoso call site.
        const currentChat = selectCurrentWaChat(state);
        const chatId: string | undefined =
            arg?.chatId
            ?? state.TeamInboxReducer?.chatViewMode?.chatId
            ?? currentChat?.id?._serialized;
        if (!chatId) return;

        // Unread lives on the team-inbox chat ENTITY (drives the sidebar
        // badge) — WhatsAppReducer.currentChat doesn't carry it.
        const chatEntity = state.DBTeamInboxChatsCacheReducer?.entities?.[chatId];
        if (!chatEntity?.unreadCount) return;

        // Debug opt-out: keep BOTH the local badge and the phone's unread state
        // intact — clearing the badge locally would hide what the toggle exists
        // to preserve.
        if (!isSendSeenEnabled()) return;

        console.log('[team-inbox] markActiveChatAsSeenIfNeeded', { chatId });

        // WA clears the badge the moment the chat opens — reset locally first,
        // then tell the phone. A failed sendSeen only means the phone keeps
        // its own unread state; the next chats fetch re-syncs us.
        dispatch(TeamInboxChatsCacheReducerActions.updateOne({
            id: chatId,
            changes: { unreadCount: 0 },
        }));

        // waSendSeenTool handles dual-mode (extension/server) internally - DRY principle
        try {
            await waSendSeenTool({ chatId });
        } catch (error) {
            console.error('[team-inbox] waSendSeenTool failed', { error });
        }
    }
);

export const requestMissingChatsImagesThunk = createAsyncThunk<
    void,
    // Explicit ids of the currently-visible rows (chats AND search-result
    // contacts, groups included). The caller resolves these from the exact
    // rendered list — passing raw virtuoso indices used to slice a *different*
    // selector (all-chats vs. the non-archived display list + header/contact
    // rows), so the batch fetched pics for the wrong chats.
    { chatIds: string[] },
    { state: CommonRootState }
>(
    "requestMissingChatsImagesThunk",
    async ({ chatIds }, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;
        const state: CommonRootState = thunkAPI.getState();

        // Get chat IDs that need profile pic updates
        // Skip if already requested in this session OR if profile pic is recent
        const chatsToUpdate = (chatIds ?? [])
            .map(chatId => {
                if (!chatId) return null;

                // Channels (@newsletter) never have profile pics — don't fetch.
                if (chatKindOf(chatId) === 'channel') return null;

                // Skip if already requested in this session
                if (requestedChatProfilePics.has(chatId)) return null;

                // Check if contact exists and has recent profile pic
                const contact = selectWhatsappContactById(state, chatId);
                const needsUpdate = !contact?.imgUrl || !contact?.profilePicLastUpdate ||
                    moment(contact.profilePicLastUpdate).isBefore(moment().subtract(1, 'day'));

                return needsUpdate ? chatId : null;
            })
            .filter(Boolean) as string[];

        // Limit batch size to 20 (same as useWaContactsLazyLoad)
        const batchToFetch = chatsToUpdate.slice(0, 20);

        if (batchToFetch.length === 0) return;

        console.log('[team-inbox] requestMissingChatsImagesThunk batch', { count: batchToFetch.length });

        // Cancel any pending profile pic request
        if (pendingProfilePicRequest) {
            pendingProfilePicRequest.abort();
            pendingProfilePicRequest = null;
        }

        // Mark these as requested immediately to avoid duplicate requests
        batchToFetch.forEach(id => requestedChatProfilePics.add(id));

        // Create abort controller for this request
        const abortController = new AbortController();
        pendingProfilePicRequest = abortController;

        try {
            // Wrapper handles fetching + upserting into DBWaContactsCacheReducer
            await dispatch(fetchAndCacheProfilePicUrlsThunk(batchToFetch)).unwrap();
        } catch (error) {
            // On error, allow retry for these contacts
            batchToFetch.forEach(id => requestedChatProfilePics.delete(id));
            console.error('[team-inbox] requestMissingChatsImagesThunk batch failed', { error });
        } finally {
            if (pendingProfilePicRequest === abortController) {
                pendingProfilePicRequest = null;
            }
        }
    }
)


export const requestChatByIdThunk = createAsyncThunk<
    void,
    // `chatId` is the id from the incoming event, which on LID accounts can be
    // the @lid form. `lastMessage`/`timestamp` are the triggering message, so
    // the chat preview updates on the SAME upsert (see keying note below).
    { chatId: string; lastMessage?: any; timestamp?: number },
    { state: CommonRootState }
>(
    "requestChatByIdThunk",
    async ({ chatId, lastMessage, timestamp }, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;

        console.log('[team-inbox] requestChatByIdThunk', { chatId });

        try {
            const result = await waGetChatObjectByChatIdTool({ chatId });

            if (result.success === false) {
                console.error('[team-inbox] waGetChatObjectByChatIdTool returned failure', { chatId, error: result.error });
                return;
            }
            // Server mode wraps the chat in { chat }; extension mode returns the chat directly.
            const data = result.data as { chat?: unknown } | unknown;
            const chat = (data && typeof data === 'object' && 'chat' in (data as object)) ? (data as { chat: unknown }).chat : data;
            if (chat) {
                // Canonical keying: the cache keys chats by chatId in @c.us form.
                // getChatObjectByChatId resolves the @c.us `phoneNumber` for
                // @lid-keyed chats — key the upsert by it so an incoming @lid
                // message lands on the EXISTING @c.us entry instead of spawning
                // a duplicate row (the duplicate-chat-on-new-message bug).
                const anyChat = chat as BtSlimChat & { phoneNumber?: string };
                const canonicalChatId = anyChat.phoneNumber ?? anyChat.chatId;
                const normalized = normalizeChat({ ...anyChat, chatId: canonicalChatId });
                // getChatObjectByChatId hands back the chat's latest *message
                // object* — which for encryption/security-code + group
                // membership/protocol events is a SYSTEM notification. WhatsApp
                // never sorts the list by those, so if the re-fetched preview is
                // one of them, keep the EXISTING real preview + sort time (from
                // the initial load) rather than overwriting it and bumping the
                // chat to the top on every stray e2e_notification. (An
                // e2e_notification keyed under the @lid form misses the @c.us
                // cache lookup in NotifierService, which is exactly what routes
                // it here as an "uncached" chat.) #chat-order-system-notif
                // `lastMessage` is carried on the entity at runtime but isn't in
                // the BtSlimChat type (only lastMessageText/…), so read it via any
                // — same shape the getChatTimestamp sort + the `{...normalized,
                // lastMessage}` merge below already rely on.
                const existing = thunkAPI.getState().DBTeamInboxChatsCacheReducer.entities[canonicalChatId] as any;
                const fetchedLastMessage = (normalized as any).lastMessage;
                const preserveRealPreview =
                    lastMessage === undefined
                    && isSystemNotificationMessage(fetchedLastMessage)
                    && existing?.lastMessage
                    && !isSystemNotificationMessage(existing.lastMessage);
                // Merge the triggering message so the preview/order update on the
                // same (deduped) entry — the sibling updateOne in NotifierService
                // no-ops when the incoming id doesn't match the canonical key.
                const withPreview = lastMessage !== undefined
                    ? { ...normalized, lastMessage, ...(timestamp !== undefined ? { timestamp } : {}) }
                    : preserveRealPreview
                        ? { ...normalized, lastMessage: existing.lastMessage, t: existing.t, timestamp: existing.timestamp }
                        : normalized;
                dispatch(TeamInboxChatsCacheReducerActions.upsertOne(withPreview));
            }
        } catch (error) {
            console.error('[team-inbox] waGetChatObjectByChatIdTool failed', { chatId, error });
        }
    }
);


export const requestContactsAndChatsBySearchThunk = createAsyncThunk<
    void,
    { debouncedSearchFilter: string },
    { state: CommonRootState }
>(
    "requestContactsAndChatsBySearchThunk",
    async ({ debouncedSearchFilter }, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;

        // Claim the latest-request slot: any older in-flight search/reload
        // becomes stale and will drop its response when it resolves.
        const requestId = beginChatListRequest();

        dispatch(TeamInboxActions.setFilteredChatAndContactList({ contacts: [], searchResponseStatus: 'loading' }));

        // Set loading state when search request starts
        dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(true));

        console.log('[team-inbox] requestContactsAndChatsBySearchThunk', { searchFilter: debouncedSearchFilter, requestId });

        // waQueryContactsAndChatsTool handles dual-mode (extension/server) internally - DRY principle
        try {
            const result = await waQueryContactsAndChatsTool({
                includeContacts: true,
                includeChats: true,
                includeLastMessage: true, // Chat rows render the last-message preview — same as the regular list
                includeArchive: true, // Search must span archived chats too (engine drops them by default)
                searchToken: debouncedSearchFilter,
            });

            // A newer search (or a full-list reload after clearing the input)
            // superseded this request while it was in flight — drop the
            // response entirely; the latest request owns the UI state.
            if (!isChatListRequestCurrent(requestId)) {
                console.log('[team-inbox] search: dropping stale response', { requestId, searchFilter: debouncedSearchFilter });
                return;
            }

            if (result.success === false) {
                console.error('[team-inbox] search: tool returned failure', { error: result.error });
                dispatch(TeamInboxActions.setFilteredChatAndContactList({ contacts: [], searchResponseStatus: 'received' }));
            } else {
                const data = result.data as { chats?: any[]; contacts?: any[] } | undefined;
                const chats = data?.chats || [];
                const contacts = data?.contacts || [];
                console.log('[team-inbox] waQueryContactsAndChatsTool result', { chatsCount: chats.length, contactsCount: contacts.length });

                if (chats.length > 0) {
                    dispatch(TeamInboxChatsCacheReducerActions.upsertMany(chats.map(normalizeChat)));
                }

                dispatch(TeamInboxActions.setFilteredChatAndContactList({
                    contacts: contacts.map((c: any) => c.id?._serialized || c.chatId),
                    searchResponseStatus: 'received'
                }));
            }
        } catch (error) {
            if (!isChatListRequestCurrent(requestId)) {
                return;
            }
            console.error('[team-inbox] waQueryContactsAndChatsTool failed', { error });
            dispatch(TeamInboxActions.setFilteredChatAndContactList({ contacts: [], searchResponseStatus: 'received' }));
        }

        // Loading flags are owned by the latest request — a superseding
        // request set itemsLoading(true) itself and will clear it when done.
        if (isChatListRequestCurrent(requestId)) {
            dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(false));
        }
    }
)

export const clearChatsCacheWithPreservationThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    "clearChatsCacheWithPreservationThunk",
    async (_, thunkAPI) => {
        const { dispatch, getState } = thunkAPI;
        const state = getState();

        // Check if there's an open chat that needs to be preserved
        const currentChatView = state.TeamInboxReducer?.chatViewMode ?? null;

        if (currentChatView?.type === "chat" && currentChatView?.chatId) {
            const currentChat = state.DBTeamInboxChatsCacheReducer?.entities?.[currentChatView?.chatId ?? ''];

            if (currentChat) {
                // Preserve the chat data in tempChatData before clearing cache
                dispatch(TeamInboxActions.preserveChatInTempData({
                    chatId: currentChatView?.chatId ?? '',
                    chatData: currentChat
                }));
            }
        }

        // Clear the cache and reset filtered list
        dispatch(TeamInboxChatsCacheReducerActions.clearState());
        dispatch(TeamInboxActions.resetFilteredChatAndContactList());
    }
)

export const clearTempChatDataIfChatAvailableThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    "clearTempChatDataIfChatAvailableThunk",
    async (_, thunkAPI) => {
        const { dispatch, getState } = thunkAPI;
        const state = getState();

        const currentChatView = state.TeamInboxReducer?.chatViewMode ?? null;

        // Clear tempChatData if the current chat is now available in cache
        if (currentChatView?.type === "chat" && currentChatView?.chatId && currentChatView?.tempChatData) {
            const currentChat = state.DBTeamInboxChatsCacheReducer?.entities?.[currentChatView?.chatId ?? ''];
            if (currentChat) {
                dispatch(TeamInboxActions.clearTempChatData());
            }
        }
    }
)


export const generateParticipantsNameListThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    'generateParticipantsNameListThunk',
    async (_, thunkAPI) => {
        const state = thunkAPI.getState();
        const dispatch = thunkAPI.dispatch;

        const currentChat = selectCurrentWaChat(state);
        const participants: BtSlimParticipant[] | undefined = currentChat?.group?.participants;
        if (!isGroupChat(currentChat) || !participants?.length) return;

        const participantIds = participants.map((p) => p.chatId);

        const nameMap = makeSelectDisplayNamesByContactIds(participantIds)(state);

        const otherNames: string[] = [];
        let hasYou = false;

        for (const name of Object.values(nameMap)) {
            if (name === 'You') {
                hasYou = true;
            } else {
                otherNames.push(name);
            }
        }
        if (hasYou) otherNames.push('You');

        const participantsDisplayNameList = otherNames.join(', ');

        dispatch(
            TeamInboxChatsCacheReducerActions.updateOne({
                id: currentChat._serialized,
                // UI-cache field (not the contract row): the header's name list.
                changes: { participantsDisplayNameList },
            })
        );
    }
);

export const requestWaMeInfoThunk = createAsyncThunk<
    void,
    void,
    { state: CommonRootState }
>(
    "requestWaMeInfoThunk",
    async (_, thunkAPI) => {
        const dispatch = thunkAPI.dispatch;

        console.log('[team-inbox] requestWaMeInfoThunk');

        // getWaMeInfoTool handles dual-mode (extension/server) internally - DRY principle
        try {
            const result = await getWaMeInfoTool();

            if (result.success === false) {
                console.error('[team-inbox] getWaMeInfoTool returned failure', { error: result.error });
                return;
            }
            // Page-handler shape carries `wid` (string | {user, _serialized}) — not in
            // GetWaMeInfoResult contract; narrow defensively until contract+handler
            // are reconciled in a follow-up slice.
            const wid = (result.data as { wid?: string | { user?: string; _serialized?: string } } | undefined)?.wid;
            if (wid) {
                // wid can be a string "972548275828@c.us" or an object { user: "972548275828", server: "c.us", _serialized: "..." }
                let phoneNumber: string;
                if (typeof wid === 'string') {
                    phoneNumber = wid.split('@')[0];
                } else if (wid._serialized) {
                    phoneNumber = wid._serialized.split('@')[0];
                } else if (wid.user) {
                    phoneNumber = wid.user;
                } else {
                    console.warn('[team-inbox] Unable to extract phone number from wid', { wid });
                    return;
                }
                dispatch(TeamInboxActions.setWaSessionPhoneNumber(phoneNumber));
            }
        } catch (error) {
            console.error('[team-inbox] getWaMeInfoTool failed', { error });
        }
    }
)

export const handleChatsInitializationThunk = createAsyncThunk<
    void,
    { debouncedSearchFilter: string },
    { state: CommonRootState }
>(
    "handleChatsInitializationThunk",
    async ({ debouncedSearchFilter }, thunkAPI) => {
        const { dispatch, getState } = thunkAPI;
        const state = getState();

        // Get current state values
        const chatsLoading = state.DBTeamInboxChatsCacheReducer.itemsLoading;
        const chatsLoadedLocally = state.DBTeamInboxChatsCacheReducer.itemsLoadedLocaly;
        const searchResponseStatus = state.TeamInboxReducer?.filteredChatAndContactList?.searchResponseStatus ?? 'noSearchValue';
        const userId = state.UserReducer.userId;

        // Compute isSearchMode
        const isSearchMode = debouncedSearchFilter.trim().length > 0;

        // SEARCH MODE: re-fire the search on EVERY debounced change (typing,
        // backspace, paste, ...). Deliberately NOT gated on itemsLoading /
        // itemsLoadedLocaly — those flags belong to whatever request is in
        // flight (and clearState() resets itemsLoadedLocaly on each search),
        // so gating on them silently dropped shrinking queries while a
        // previous search was still loading. Out-of-order responses are
        // handled inside requestContactsAndChatsBySearchThunk via request ids.
        if (isSearchMode) {
            dispatch(clearChatsCacheWithPreservationThunk());
            dispatch(requestContactsAndChatsBySearchThunk({ debouncedSearchFilter }));
            return;
        }

        // NON-SEARCH MODE: initial load, or restore of the regular list after
        // leaving search. Any status other than 'noSearchValue' means the
        // cache currently holds search results and must be rebuilt.
        const wasInSearchMode = searchResponseStatus !== 'noSearchValue';
        const shouldLoadChats = (!!userId && !chatsLoadedLocally) || wasInSearchMode;

        if (!shouldLoadChats) {
            return;
        }

        // Dedupe concurrent initial loads — but never block the restore-after-
        // search path: an in-flight search keeps itemsLoading=true and would
        // otherwise leave the list stuck on (stale) search results.
        if (chatsLoading && !wasInSearchMode) {
            return;
        }

        {
            // Claim the latest-request slot — this also invalidates any
            // in-flight search so its late response cannot overwrite the
            // restored regular list.
            const requestId = beginChatListRequest();

            // Leave search mode for real: reset the search status so the next
            // init run doesn't read a stale 'received'/'loading' and re-trigger
            // another full reload (render churn + permanent spinner).
            dispatch(TeamInboxActions.setFilteredChatAndContactList({ contacts: [], searchResponseStatus: 'noSearchValue' }));

            // Reset state and load initial chats
            dispatch(clearChatsCacheWithPreservationThunk());

            // Load contacts if not already loaded locally
            const contactsLoadedLocally = state.DBWaContactsCacheReducer.itemsLoadedLocaly;
            const shouldGetMoreContacts = shouldGetMoreContactsSelector(state);

            if (!contactsLoadedLocally || !shouldGetMoreContacts) {
                dispatch(DBWaContactsCacheThunks.find({}));
            }

            if (!state.TeamInboxReducer?.waSessionPhoneNumber) {
                dispatch(requestWaMeInfoThunk());
            }

            // Fetch ALL chats at once (no pagination) to ensure archived chats are included
            dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(true));
            try {
                const result = await waQueryContactsAndChatsTool({
                    includeContacts: false,
                    includeChats: true,
                    includeLastMessage: true,
                    // The engine drops archived chats unless explicitly asked;
                    // the sidebar partitions archived vs. inbox client-side, so
                    // we must fetch them here or the Archived folder stays empty.
                    includeArchive: true,
                    limit: 10000,
                    offset: 0,
                });

                if (!isChatListRequestCurrent(requestId)) {
                    // The user started a search while the full list was
                    // loading — don't pollute the search results with it.
                    console.log('[team-inbox] init chats: dropping stale response', { requestId });
                    return;
                }

                if (result.success === false) {
                    console.error('[team-inbox] init chats: tool returned failure', { error: result.error });
                } else {
                    const data = result.data as { chats?: any[] } | undefined;
                    const chats = data?.chats || [];
                    if (chats.length > 0) {
                        dispatch(TeamInboxChatsCacheReducerActions.upsertMany(chats.map(normalizeChat)));
                        dispatch(TeamInboxActions.setHasMoreChatsToLoad(chats.length >= 10000));
                    }
                }
            } catch (error) {
                if (!isChatListRequestCurrent(requestId)) {
                    return;
                }
                console.error('[team-inbox] waQueryContactsAndChatsTool failed', { error });
            }
            dispatch(TeamInboxChatsCacheReducerActions.setItemsLoading(false));
            dispatch(TeamInboxChatsCacheReducerActions.setItemsLoadedLocaly(true));
        }
    }
)
